import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getDateRangeValue, getRangeValue } from '../../../../packages/JsonQuery';
// TODO[CD-268396]: на десктопе сейчас отправляются мобильные события (Mob_*). Ждём категории от аналитики,
// после — завести src/shared/ListingDesktop/tracking и заменить импорты ниже. Подробности в техдолге Obsidian.
import { onHideOffer } from '../../../common/actions/hiddenObjects';
import { onAddToFavorite as addToFavorites } from '../../../common/actions/newFavorites/onAddToFavorite';
import { removeFromFavorites } from '../../../common/actions/newFavorites/removeFromFavorite';
import { FavoriteButton } from '../../../common/components/FavoriteButton';
import { GalleryMoreMenu } from '../../../common/components/GalleryMoreMenu';
import { OfferCardHorizontal } from '../../../common/components/OfferCardHorizontal';
import { useApplicationContext } from '../../../common/containers/ApplicationContext';
import { useComplainModalContext } from '../../../common/containers/ComplainModalProvider';
import { useTrackingProps } from '../../../common/hooks/useTrackingProps';
import { IDailyRentOfferForMobileSite } from '../../../common/repositories/dailyrent/entities/daily_rent_offers_for_web/DailyRentOfferForMobileSite';
import { selectMlSearchSessionGuid } from '../../../common/selectors/analytics';
import { selectListingMlRanking } from '../../../common/selectors/listingOffers/selectListingMlRanking';
import { selectResultsJsonQuery } from '../../../common/selectors/results';
import { selectIsUserAgent } from '../../../common/selectors/user';
import {
  trackAddFavorite,
  trackBookClick,
  trackCall,
  trackChatClick,
  trackHideOffer,
  trackShowPhoneClick,
  trackSlideChange,
  trackBuildingClick,
  trackMoreClick,
  trackOfferCardShow,
  trackRemoveFromFavorite,
} from '../../../common/tracking';
import { TThunkDispatch } from '../../../common/types/redux';
import { getIsBrandingBlockVisible } from '../../../common/utils/getIsBrandingBlockVisible';
import { getOfferLinkRel } from '../../../common/utils/getOfferLinkRel';
import { prepareOfferUrl } from '../../../common/utils/prepareOfferUrl';
import { defferedTrack } from '../../../common/utils/tracking';

export interface IOfferCardDesktopContainerProps {
  offer: IDailyRentOfferForMobileSite;
  offerPosition: number;
  offersLength: number;
  /** recommendation — карточка блока «Вам могут подойти»: без кнопок действий, рейтинга и избранного */
  variant?: 'listing' | 'recommendation';
  onMouseEnter?: (realtyId: number) => void;
  onMouseLeave?: (realtyId: number) => void;
  onCardClick(): void;
}

export const OfferCardDesktopContainer = ({
  offer,
  offerPosition,
  offersLength,
  variant = 'listing',
  onMouseEnter,
  onMouseLeave,
  onCardClick,
}: IOfferCardDesktopContainerProps) => {
  const { config } = useApplicationContext();
  const dispatch = useDispatch<TThunkDispatch>();
  const jsonQuery = useSelector(selectResultsJsonQuery);
  const mlRanking = useSelector(selectListingMlRanking);
  const mlSearchSessionGuid = useSelector(selectMlSearchSessionGuid);
  const isUserAgent = useSelector(selectIsUserAgent);
  const isSliderEventTriggeredRef = React.useRef(false);
  const isPartnerFavoritesEnabled = !!config.get<boolean>('dailyrent.partners_favorites.Enabled');
  const promotionDescriptionLearnMoreUrl =
    config.get<string>('Dailyrent.PromotionDescription.LearnMoreUrl') || undefined;
  const jsonQueryDates = getDateRangeValue('dates')(jsonQuery);
  const checkInDate = jsonQueryDates?.gte;
  const checkOutDate = jsonQueryDates?.lt;
  const minBeds = getRangeValue('beds_count')(jsonQuery)?.gte;
  const isRecommendation = variant === 'recommendation';
  const isFavoriteButtonVisible = (!offer.isCianPartner || isPartnerFavoritesEnabled) && !offer.isHotel;
  const showBrandingBlock = !isRecommendation && getIsBrandingBlockVisible(offer);
  const hasDates = !!jsonQueryDates;

  const offerLink = prepareOfferUrl({
    offer,
    checkInDate,
    checkOutDate,
    minBeds,
  });
  const offerLinkRel = getOfferLinkRel(offer);
  const trackingProps = useTrackingProps({ offer, offerPosition, offersLength, reason: 'all_objects_on_map' });

  const onMoreClick = React.useCallback(() => {
    trackMoreClick(trackingProps);
  }, [trackingProps]);

  const onBookClick = React.useCallback(() => {
    trackBookClick(trackingProps);
  }, [trackingProps]);

  const onCallClick = React.useCallback(() => {
    trackShowPhoneClick(trackingProps);
    defferedTrack(() => trackCall(trackingProps));
  }, [trackingProps]);

  const onChatClick = React.useCallback(() => {
    trackChatClick(trackingProps);
  }, [trackingProps]);

  const onSlideChange = React.useCallback(() => {
    if (!isSliderEventTriggeredRef.current) {
      trackSlideChange(trackingProps);
      isSliderEventTriggeredRef.current = true;
    }
  }, [trackingProps]);

  const onBuildingClick = React.useCallback(() => {
    trackBuildingClick(trackingProps);
  }, [trackingProps]);

  const handleMouseEnter = React.useCallback(() => {
    onMouseEnter?.(offer.id);
  }, [offer.id]);

  const handleMouseLeave = React.useCallback(() => {
    onMouseLeave?.(offer.id);
  }, [offer.id]);

  const onAddToFavorites = React.useCallback(() => {
    dispatch(addToFavorites(offer));
    trackAddFavorite(trackingProps);
  }, [dispatch, offer, trackingProps]);

  const onRemoveFromFavorites = React.useCallback(() => {
    dispatch(removeFromFavorites(offer));
    trackRemoveFromFavorite(trackingProps);
  }, [dispatch, offer, trackingProps]);

  const onHide = React.useCallback(() => {
    dispatch(onHideOffer(offer));
    trackHideOffer(trackingProps);
  }, [dispatch, offer, trackingProps]);

  const { openComplain } = useComplainModalContext();

  const onReport = React.useCallback(() => openComplain(offer.id), [openComplain, offer.id]);

  const onCardScrollIntoView = () => {
    trackOfferCardShow(trackingProps, mlRanking, mlSearchSessionGuid);
  };

  const carouselControls = React.useMemo(() => {
    return (
      <>
        {isFavoriteButtonVisible && (
          <FavoriteButton isFavorite={offer.isFavorite} onAdd={onAddToFavorites} onRemove={onRemoveFromFavorites} />
        )}
        {!isRecommendation && <GalleryMoreMenu onHide={onHide} onReport={onReport} />}
      </>
    );
  }, [
    isRecommendation,
    isFavoriteButtonVisible,
    offer.isFavorite,
    onAddToFavorites,
    onRemoveFromFavorites,
    onHide,
    onReport,
  ]);

  return (
    <ActionAfterViewObserver
      percentVisible={0.5}
      callback={onCardScrollIntoView}
      triggerOnce={false}
      callbackDelay={1000}
    >
      <OfferCardHorizontal
        offer={offer}
        hasDates={hasDates}
        showBrandingBlock={showBrandingBlock}
        showThumbnails
        showButtons={!isRecommendation}
        showRating={!isRecommendation}
        showPromotionLabels={isUserAgent && !isRecommendation}
        priceVariant={isRecommendation ? 'recommendation' : 'default'}
        carouselControls={carouselControls}
        isSingle={false}
        offerLink={offerLink}
        offerLinkRel={offerLinkRel}
        promotionDescriptionLearnMoreUrl={promotionDescriptionLearnMoreUrl}
        onCardClick={onCardClick}
        onMoreClick={onMoreClick}
        onBookClick={onBookClick}
        onCallClick={onCallClick}
        onChatClick={onChatClick}
        onSlideChange={onSlideChange}
        onBuildingClick={onBuildingClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
    </ActionAfterViewObserver>
  );
};
